import React from "react";
import styled from "styled-components";

import PageWrapper from "../components/PageWrapper";
import ElearningGrid from "../sections/academy/ElearningGrid";
import CTA from "../sections/landing4/CTA";
import Seo from "../components/Seo";
import Instructions from "../components/Instructions/Instructions";
import ProgressGrid from "../sections/gamification/ProgressGrid";
import elearningData from "../data/elearning-modules.json";

const TeaserWrap = styled.section`
  margin: 48px 0 16px;
  display: flex;
  justify-content: center;
`;

const TeaserCard = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  max-width: 1100px;
  width: 100%;
  padding: 28px 32px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 12px;
  text-decoration: none !important;
  transition: border-color 200ms cubic-bezier(0.25, 0.1, 0.25, 1);

  &:hover {
    border-color: var(--nd-text-display);
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    gap: 20px;
  }
`;

const TeaserText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const TeaserLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const TeaserHeading = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0;
  letter-spacing: -0.01em;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const TeaserSubline = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: var(--nd-text-secondary, var(--nd-text-primary));
  margin: 0;
  max-width: 560px;
`;

const TeaserCta = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  border-bottom: 1px solid var(--nd-border-visible);
  align-self: flex-start;
  padding-bottom: 2px;
  margin-top: 4px;
`;

const LabsTeaser = () => (
  <TeaserWrap>
    <TeaserCard href="/labs" title="18 of 25 labs unlocked. One challenge away from the next.">
      <TeaserText>
        <TeaserLabel>Hands-on · Gamified</TeaserLabel>
        <TeaserHeading>Six courses watched. Eighteen labs unlocked.</TeaserHeading>
        <TeaserSubline>
          Each course module maps to real-environment challenges. Earn lives,
          hold streaks, top the leaderboard — or just learn faster.
        </TeaserSubline>
        <TeaserCta>Claim your labs →</TeaserCta>
      </TeaserText>
      <ProgressGrid filled={18} rows={5} cols={5} pulsingNext ariaLabel="18 of 25 labs unlocked" />
    </TeaserCard>
  </TeaserWrap>
);

const AcademyPage = () => {
  return (
    <>
      <Seo
        title="Dioscuro Academy — DevOps Courses"
        description="Short, dense, opinionated DevOps courses. Free preview per module, AI mentor scoped to the curriculum."
        pathname="/academy"
      />
      <PageWrapper>
        <ElearningGrid
          modules={elearningData.modules}
          instructions={
            <Instructions label="how to use the academy">
              <h4>What's here</h4>
              <p>
                Seven short, opinionated DevOps courses. Each has a free preview
                lesson and an AI mentor scoped to the curriculum.
              </p>

              <h4>How to start</h4>
              <ol>
                <li>Browse the grid. Open any course.</li>
                <li>Watch the free preview lesson.</li>
                <li>
                  Ask the AI mentor a question from the module's curriculum —
                  it's scoped, not a chatbot.
                </li>
                <li>
                  Click <strong>Enroll</strong> to track progress (you'll need
                  to sign in).
                </li>
              </ol>

              <h4>Want hands-on practice?</h4>
              <p>
                Pair courses with <a href="/labs">DevOps Labs</a> — same
                curriculum, but you build real systems instead of just watching.
              </p>
            </Instructions>
          }
        />
        <LabsTeaser />
        <CTA />
      </PageWrapper>
    </>
  );
};
export default AcademyPage;
